import React, { FC, useMemo, useState } from 'react'
import { Button } from '@mui/material'
import { BulkAssignSensorsConfirmationModal } from 'src/components/forms'
import { StatefulDialog } from 'src/components/ui'
import { DialogNames } from 'src/enums'
import { buildBulkResultMessages } from 'src/helpers'
import { useAssignSensorsBulk } from 'src/hooks/api'
import { useAddPopupMessage, useDialog } from 'src/hooks/ui'
import { AssignSensorToContractor } from '../assignSensorToContractor'
import { SensorQualityControlDialog } from '../sensorQualityControl'

interface SelectedSensor {
  uniqueId: string
  imei: string | number | null
}

interface BulkAssignSensorsButtonProps {
  selectedSensors: SelectedSensor[]
  onSuccess?: () => void
}

export const BulkAssignSensorsButton: FC<BulkAssignSensorsButtonProps> = ({ selectedSensors, onSuccess }) => {
  const [bulkAssignContractor, setBulkAssignContractor] = useState<{ id: number | null; name: string } | null>(null)
  const addPopupMessage = useAddPopupMessage()
  const { openDialog: openPickerDialog } = useDialog(DialogNames.AssignSensorsBulkPicker)
  const { openDialog: openConfirmDialog, closeDialog: closeConfirmDialog } = useDialog(DialogNames.AssignSensorsBulkConfirmation)
  const { openDialog: openQualityControlDialog, closeDialog: closeQualityControlDialog } = useDialog(DialogNames.AssignSensorsQualityControl)
  const selectedUniqueIds = useMemo(() => selectedSensors.map((sensor) => sensor.uniqueId), [selectedSensors])

  const assignSensorsBulk = useAssignSensorsBulk({
    options: {
      onSuccess: (results) => {
        const failed = results.filter((item) => item.status !== 'ok')
        addPopupMessage(
          buildBulkResultMessages(
            results,
            (count) => `Successfully assigned ${count} sensor${count !== 1 ? 's' : ''}.`,
            (uniqueId, message) => `Failed to assign sensor ${uniqueId}: ${message}`,
          ),
        )
        if (failed.length === 0) closeConfirmDialog()
        onSuccess?.()
      },
      onError: (error) => {
        addPopupMessage({ text: error.message, type: 'error' })
      },
    },
  })

  const handleContractorSelected = (contractorId: number | null, contractorName: string) => {
    setBulkAssignContractor({ id: contractorId, name: contractorName })
    openConfirmDialog()
  }

  return (
    <>
      <Button size="large" variant="outlined" onClick={openQualityControlDialog}>
        Assign
      </Button>
      <SensorQualityControlDialog
        name={DialogNames.AssignSensorsQualityControl}
        uniqueIds={selectedUniqueIds}
        title="Quality Control - review before assigning"
        confirmLabel="Continue"
        onConfirm={() => closeQualityControlDialog(openPickerDialog)}
        onClose={() => closeQualityControlDialog()}
      />
      <StatefulDialog name={DialogNames.AssignSensorsBulkPicker} wrapContent={false}>
        <AssignSensorToContractor
          onSubmit={handleContractorSelected}
          dialogName={DialogNames.AssignSensorsBulkPicker}
        />
      </StatefulDialog>
      <BulkAssignSensorsConfirmationModal
        selectedSensors={selectedSensors}
        contractorName={bulkAssignContractor?.name || ''}
        onConfirm={() => {
          if (bulkAssignContractor !== null) {
            assignSensorsBulk.mutate({
              uniqueIds: selectedSensors.map((s) => s.uniqueId),
              contractorId: bulkAssignContractor.id,
            })
          }
        }}
        onClose={() => closeConfirmDialog()}
        isLoading={assignSensorsBulk.isLoading}
      />
    </>
  )
}
